import React, { useState } from 'react'
import { ShieldAlert, Check, X, Code2, ListTree, ChevronDown, ChevronRight } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { PendingApproval } from '../services/chatApi'

// tool.arguments comes from the provider's streamed function-call arguments — normally a
// valid JSON string, but not guaranteed (empty/partial stream, etc.). An unguarded JSON.parse
// here previously threw during render with no error boundary around it, which silently failed
// to mount the entire approval card — leaving the user stuck with a pending backend interrupt
// and no visible way to approve/reject/edit it.
function safeFormatArguments(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}

function safeParseArguments(raw: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>
    }
    return null
  } catch {
    return null
  }
}

// Known external providers Zeph integrates with (see CLAUDE.md §7) — used only to
// prettify the badge/title; unrecognized prefixes fall back to a generic title-case.
const PROVIDER_LABELS: Record<string, string> = {
  hubspot: 'HubSpot',
  pipedrive: 'Pipedrive',
  salesforce: 'Salesforce',
  slack: 'Slack',
  gmail: 'Gmail',
  outlook: 'Outlook',
  teams: 'Teams',
  zoom: 'Zoom',
  googlemeet: 'Google Meet',
}

function humanizeToolName(name: string): { title: string; provider?: string } {
  const parts = name.split('_').filter(Boolean)
  const provider = parts.length > 1 ? PROVIDER_LABELS[parts[0].toLowerCase()] : undefined
  const rest = provider ? parts.slice(1) : parts
  const title = rest.map((w) => w[0].toUpperCase() + w.slice(1)).join(' ')
  return { title: title || name, provider }
}

function humanizeKey(key: string): string {
  const withSpaces = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/_/g, ' ')
  return withSpaces
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ')
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

// Fields most likely to identify *what* the action is about, checked in order.
const SUMMARY_KEY_PRIORITY = [
  'name',
  'firstname',
  'first_name',
  'lastname',
  'last_name',
  'email',
  'dealname',
  'deal_name',
  'subject',
  'title',
  'company',
  'phone',
]

function summarizeArguments(raw: string): string | null {
  const obj = safeParseArguments(raw)
  if (!obj) return null

  const entries = Object.entries(obj).filter(
    ([, v]) => (typeof v === 'string' && v.trim().length > 0) || typeof v === 'number',
  )

  entries.sort(([a], [b]) => {
    const rank = (k: string) => {
      const i = SUMMARY_KEY_PRIORITY.indexOf(k.toLowerCase())
      return i === -1 ? SUMMARY_KEY_PRIORITY.length : i
    }
    return rank(a) - rank(b)
  })

  const values = entries
    .slice(0, 3)
    .map(([, v]) => String(v))
    .filter((v) => v.length <= 60)

  return values.length > 0 ? values.join(' · ') : null
}

interface ApprovalCardProps {
  pendingApproval: {
    conversationId: string
    tools: PendingApproval[]
  }
  onDecision: (decision: { action: 'approved' | 'rejected' | 'edited'; editedToolCalls?: any[] }) => void
}

export const ApprovalCard: React.FC<ApprovalCardProps> = ({ pendingApproval, onDecision }) => {
  const { currentTheme } = useTheme()
  const [isEditing, setIsEditing] = useState(false)
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({})
  const [editedTools, setEditedTools] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {}
    pendingApproval.tools.forEach((t) => {
      initial[t.toolCallId] = safeFormatArguments(t.arguments)
    })
    return initial
  })

  const [parseErrors, setParseErrors] = useState<Record<string, string>>({})

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const toggleEditing = () => {
    const next = !isEditing
    setIsEditing(next)
    setParseErrors({})
    if (next) {
      // Editing raw JSON only makes sense once the row is visible.
      const allExpanded: Record<string, boolean> = {}
      pendingApproval.tools.forEach((t) => {
        allExpanded[t.toolCallId] = true
      })
      setExpandedIds(allExpanded)
    }
  }

  const handleArgChange = (id: string, value: string) => {
    setEditedTools((prev) => ({ ...prev, [id]: value }))
    // Clear error on edit
    if (parseErrors[id]) {
      setParseErrors((prev) => {
        const copy = { ...prev }
        delete copy[id]
        return copy
      })
    }
  }

  const handleEditSubmit = () => {
    const newErrors: Record<string, string> = {}
    const updatedCalls: any[] = []

    pendingApproval.tools.forEach((t) => {
      const rawText = editedTools[t.toolCallId] || '{}'
      try {
        const parsed = JSON.parse(rawText)
        updatedCalls.push({
          id: t.toolCallId,
          name: t.toolName,
          arguments: JSON.stringify(parsed),
        })
      } catch (err: any) {
        newErrors[t.toolCallId] = err.message || 'Invalid JSON format'
      }
    })

    if (Object.keys(newErrors).length > 0) {
      setParseErrors(newErrors)
      return
    }

    onDecision({
      action: 'edited',
      editedToolCalls: updatedCalls,
    })
  }

  return (
    <div className="px-4 py-3 shrink-0 max-w-[60rem] mx-auto w-full">
      <div
        className="rounded-xl border p-4 flex flex-col gap-3 transition-all duration-300"
        style={{
          backgroundColor: currentTheme.cardBackground || currentTheme.secondary,
          borderColor: currentTheme.border,
        }}
      >
        {/* Label */}
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-amber-500" />
          <span className="text-sm font-semibold" style={{ color: currentTheme.text }}>
            Approval Required
          </span>
        </div>

        {/* Tools list */}
        <div className="flex flex-col gap-2">
          {pendingApproval.tools.map((tool) => {
            const hasError = parseErrors[tool.toolCallId]
            const isExpanded = !!expandedIds[tool.toolCallId]
            const { title, provider } = humanizeToolName(tool.toolName)
            const summary = summarizeArguments(tool.arguments)
            const parsedArgs = safeParseArguments(tool.arguments)

            return (
              <div
                key={tool.toolCallId}
                className="rounded-xl border flex flex-col"
                style={{
                  backgroundColor: currentTheme.activeBackground,
                  borderColor: currentTheme.border,
                }}
              >
                <button
                  type="button"
                  onClick={() => toggleExpanded(tool.toolCallId)}
                  className="flex items-start justify-between gap-3 p-3.5 text-left w-full"
                >
                  <div className="flex items-start gap-2.5 min-w-0">
                    <span className="mt-0.5 shrink-0" style={{ color: currentTheme.mutedText }}>
                      {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    </span>
                    <div className="min-w-0 flex flex-col gap-0.5">
                      <span className="text-sm font-semibold" style={{ color: currentTheme.text }}>
                        {title}
                        {provider && (
                          <span
                            className="ml-2 text-[10px] uppercase tracking-wider font-semibold opacity-70 px-2 py-0.5 rounded bg-indigo-500/10 text-indigo-400"
                          >
                            {provider}
                          </span>
                        )}
                      </span>
                      {summary && (
                        <span className="text-xs truncate" style={{ color: currentTheme.mutedText }}>
                          {summary}
                        </span>
                      )}
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 flex flex-col gap-2">
                    {isEditing ? (
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold" style={{ color: currentTheme.mutedText }}>
                          Parameters (JSON)
                        </label>
                        <textarea
                          value={editedTools[tool.toolCallId]}
                          onChange={(e) => handleArgChange(tool.toolCallId, e.target.value)}
                          className="w-full min-h-[100px] text-xs font-mono p-3 rounded-lg border outline-none bg-black/20 text-slate-100"
                          style={{
                            borderColor: hasError ? '#ef4444' : currentTheme.border,
                          }}
                        />
                        {hasError && (
                          <span className="text-red-500 text-[10px] font-semibold">
                            Error parsing JSON: {hasError}
                          </span>
                        )}
                      </div>
                    ) : parsedArgs && Object.keys(parsedArgs).length > 0 ? (
                      <div className="rounded-lg border overflow-hidden" style={{ borderColor: currentTheme.border }}>
                        {Object.entries(parsedArgs).map(([key, value], idx) => (
                          <div
                            key={key}
                            className="flex items-start justify-between gap-4 px-3 py-2 text-xs"
                            style={{
                              borderTop: idx === 0 ? 'none' : `1px solid ${currentTheme.border}`,
                            }}
                          >
                            <span className="shrink-0" style={{ color: currentTheme.mutedText }}>
                              {humanizeKey(key)}
                            </span>
                            <span className="text-right break-all font-mono" style={{ color: currentTheme.text }}>
                              {formatValue(value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <pre
                        className="text-xs font-mono p-3 rounded-lg bg-black/10 overflow-x-auto border"
                        style={{ borderColor: currentTheme.border, color: currentTheme.text }}
                      >
                        {safeFormatArguments(tool.arguments)}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Actions bar */}
        <div className="flex justify-between items-center gap-3 pt-2 border-t" style={{ borderColor: currentTheme.border }}>
          {/* Toggle Edit mode */}
          <button
            onClick={toggleEditing}
            className="flex items-center gap-2 text-xs font-semibold py-2 px-3.5 rounded-xl border transition-all duration-200"
            style={{
              backgroundColor: currentTheme.primary,
              borderColor: currentTheme.border,
              color: currentTheme.text,
            }}
          >
            {isEditing ? (
              <>
                <ListTree className="w-3.5 h-3.5" />
                <span>Done Editing</span>
              </>
            ) : (
              <>
                <Code2 className="w-3.5 h-3.5" />
                <span>Edit as JSON</span>
              </>
            )}
          </button>

          {/* Action Decision buttons */}
          <div className="flex items-center gap-3">
            {/* Reject */}
            <button
              onClick={() => onDecision({ action: 'rejected' })}
              className="flex items-center gap-2 text-xs font-semibold py-2 px-4 rounded-xl transition-all duration-200 bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/20"
            >
              <X className="w-3.5 h-3.5" />
              <span>Reject</span>
            </button>

            {/* Approve / Save changes */}
            {isEditing ? (
              <button
                onClick={handleEditSubmit}
                className="flex items-center gap-2 text-xs font-semibold py-2 px-4 rounded-xl transition-all duration-200 text-white"
                style={{
                  background: currentTheme.accentGradient,
                }}
              >
                <Check className="w-3.5 h-3.5" />
                <span>Submit Changes</span>
              </button>
            ) : (
              <button
                onClick={() => onDecision({ action: 'approved' })}
                className="flex items-center gap-2 text-xs font-semibold py-2 px-4 rounded-xl transition-all duration-200 text-white"
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                }}
              >
                <Check className="w-3.5 h-3.5" />
                <span>Approve & Execute</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
